import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Popup } from 'devextreme-react/popup';
import Button from 'devextreme-react/button';
import moment from 'moment';
import useApiRem from '../../../../../api/api.remmisions';
import { useAppSelector } from '../../../../../contexts/hooks';
import { ETypeRemission, RemisionEstudioInfo } from '../../../../../types/types.remissions';
import './StudyRemissionInfo.scss';

// La vista completa es la misma del módulo Remisiones. Va diferida porque arrastra el visor de
// reportes (DevExpress): no debe pesar en la pestaña Estudios hasta que alguien la abra.
const ModalViewRemision = lazy(() => import('../../../../../pages/referrals/Modals/ModalViewRemision').then((m) => ({ default: m.ModalViewRemision })));

// Mismos nombres y colores que la vista de remisiones (pages/referrals/Modals/ModalViewRemision).
const ESTADOS: Record<number, { nombre: string; color: string; bgColor: string }> = {
  [ETypeRemission.CREADA]: { nombre: 'CREADA', color: '#6c757d', bgColor: '#f8f9fa' },
  [ETypeRemission.IMPRESA]: { nombre: 'IMPRESA', color: '#0d6efd', bgColor: '#e7f1ff' },
  [ETypeRemission.ENVIADA]: { nombre: 'ENVIADA', color: '#fd7e14', bgColor: '#fff3e0' },
  [ETypeRemission.EN_PROCESO]: { nombre: 'EN PROCESO', color: '#ffc107', bgColor: '#fff8e1' },
  [ETypeRemission.COMPLETADA]: { nombre: 'COMPLETADA', color: '#198754', bgColor: '#e8f5e8' },
  [ETypeRemission.CANCELADA]: { nombre: 'CANCELADA', color: '#dc3545', bgColor: '#ffe6e6' },
};

const fecha = (valor?: string | null) => (valor ? moment(valor).format('DD/MM/YYYY hh:mm A') : null);

/** Enviada = ya salió al laboratorio: tiene fecha de envío o un estado posterior al envío. */
const fueEnviada = (info: RemisionEstudioInfo) => !!info.Rm_FechaEnvio || [ETypeRemission.ENVIADA, ETypeRemission.EN_PROCESO, ETypeRemission.COMPLETADA].includes(info.Rm_Estado);

/**
 * Ventana de la columna REMITIDO de la pestaña Estudios: en qué remisión salió el estudio, a qué
 * laboratorio, quién la creó, si ya se envió y cuándo. Se consulta al abrirla, sin caché, para
 * que el estado sea el de ese momento.
 *
 * Se muestra UNA vez y ya con los datos: si se abriera primero con un indicador de carga, el
 * Popup (alto automático) crece y se vuelve a centrar al llegar la respuesta, y eso se ve como
 * una segunda apertura. "Ver remisión" la reemplaza por la vista completa del módulo Remisiones;
 * al cerrar esa, se cierra todo.
 */
export const ModalStudyRemissionInfo = ({ orderId, study, onClose }: { orderId: number; study: { Es_Id: number; Es_Nombre: string }; onClose: () => void }) => {
  const { getStudyRemissionInfo } = useApiRem();
  const [info, setInfo] = useState<RemisionEstudioInfo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [verRemision, setVerRemision] = useState(false);
  // onHidden también salta al ceder el lugar a la vista completa; ahí no hay que cerrar.
  const pasandoAVistaCompleta = useRef(false);
  // La vista completa usa Remission/GetRemision, que exige el permiso del módulo Remisiones.
  const puedeVerRemisiones = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Remisiones_R) === true;

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    getStudyRemissionInfo(orderId, study.Es_Id).then((res) => {
      if (!vigente) return;
      setInfo(res);
      setCargando(false);
    });
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, study.Es_Id]);

  const handleVerRemision = () => {
    pasandoAVistaCompleta.current = true;
    setVerRemision(true);
  };

  const handleHidden = () => {
    if (!pasandoAVistaCompleta.current) onClose();
  };

  const estado = info ? ESTADOS[info.Rm_Estado] ?? { nombre: 'DESCONOCIDO', color: '#6c757d', bgColor: '#f8f9fa' } : null;

  return (
    <>
      <Popup visible={!cargando && !verRemision} onHidden={handleHidden} width={440} height='auto' maxHeight='90%' title='REMISIÓN DEL ESTUDIO' showTitle={true} showCloseButton={true} hideOnOutsideClick={true}>
        <div className='study-remission-info'>
          <div className='sri-estudio'>{study.Es_Nombre}</div>

          {!info && <div className='sri-vacio'>El estudio no está incluido en ninguna remisión.</div>}

          {info && estado && (
            <dl className='sri-datos'>
              <dt>Remisión No.</dt>
              <dd>
                <b>{info.Rm_Id}</b>
                {info.Rmd_Urgente && <span className='sri-urgente'>URGENTE</span>}
              </dd>

              <dt>Estado</dt>
              <dd>
                <span className='sri-estado' style={{ color: estado.color, backgroundColor: estado.bgColor, borderColor: estado.color }}>
                  {estado.nombre}
                </span>
              </dd>

              <dt>Laboratorio</dt>
              <dd>{info.Laboratorio || '—'}</dd>

              <dt>Creada por</dt>
              <dd>
                {info.CreadaPor || '—'}
                <div className='sri-sub'>{fecha(info.Rm_FechaCreacion)}</div>
              </dd>

              <dt>Enviada</dt>
              <dd>
                {fueEnviada(info) ? (
                  <>
                    <span className='sri-si'>Sí</span>
                    {info.Rm_FechaEnvio && <div className='sri-sub'>{fecha(info.Rm_FechaEnvio)}</div>}
                    {info.Rm_ResponsableEnvio && <div className='sri-sub'>Responsable: {info.Rm_ResponsableEnvio}</div>}
                  </>
                ) : (
                  <span className='sri-no'>{info.Rm_Estado === ETypeRemission.CANCELADA ? 'No, la remisión fue cancelada' : 'Aún no'}</span>
                )}
              </dd>

              <dt>Resultado</dt>
              <dd>{info.Rmd_ResultadoRecibido ? <>Recibido{info.Rmd_FechaResultado && <div className='sri-sub'>{fecha(info.Rmd_FechaResultado)}</div>}</> : 'Pendiente'}</dd>
            </dl>
          )}

          <div className='sri-acciones' style={{ display: info && puedeVerRemisiones ? undefined : 'none' }}>
            <Button text='Ver remisión' icon='search' type='default' stylingMode='contained' onClick={handleVerRemision} />
          </div>
        </div>
      </Popup>

      {/* Fuera del Popup pequeño: este se oculta y la vista completa ocupa su lugar. */}
      {verRemision && info && (
        <Suspense fallback={null}>
          <ModalViewRemision visible={true} remisionId={info.Rm_Id} onClose={onClose} wrapperAttr={{ class: 'view-remision-modal' }} />
        </Suspense>
      )}
    </>
  );
};
